// ecommerce-frontend/src/pages/LoginPage.jsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LogIn, User, Lock, AlertCircle, Eye, EyeOff, CheckCircle, X
} from 'lucide-react';
import { authAPI } from '../services/api';

// Styles d'animation CSS
const styles = `
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
    20%, 40%, 60%, 80% { transform: translateX(10px); }
  }

  @keyframes wiggle {
    0%, 100% { transform: rotate(0deg); }
    25% { transform: rotate(-5deg); }
    75% { transform: rotate(5deg); }
  }

  @keyframes pulse-glow {
    0%, 100% { box-shadow: 0 0 20px rgba(251, 146, 60, 0.5); }
    50% { box-shadow: 0 0 40px rgba(251, 146, 60, 0.8); }
  }

  .shake-animation {
    animation: shake 0.5s ease-in-out;
  }

  .wiggle-animation {
    animation: wiggle 0.5s ease-in-out;
  }

  .pulse-glow-animation {
    animation: pulse-glow 2s ease-in-out infinite;
  }

  .icon-container {
    transition: transform 0.3s ease;
  }

  .icon-container:hover {
    transform: scale(1.1) rotate(5deg);
  }
`;

export default function LoginPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [statusMessage, setStatusMessage] = useState({ type: null, text: '' }); 
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [shakeIcon, setShakeIcon] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (statusMessage.type) setStatusMessage({ type: null, text: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMessage({ type: null, text: '' });
    setLoading(true);

    try {
      await authAPI.login(formData);
      
      setStatusMessage({ 
        type: 'success', 
        text: 'Connexion réussie ! Redirection vers la page d\'accueil...'
      });
      
      setTimeout(() => {
        navigate('/'); 
      }, 3000); 

    } catch (err) {
      let generalError = 'Erreur lors de la connexion. Veuillez réessayer.';

      if (err.response && err.response.data) {
        const errorData = err.response.data;
        
        const errorMessages = {
          'Invalid credentials': 'Nom d\'utilisateur ou mot de passe incorrect',
          'User account is disabled': 'Ce compte a été désactivé',
          'No active account found': 'Aucun compte actif trouvé avec ces identifiants',
          'Unable to log in with provided credentials': 'Nom d\'utilisateur ou mot de passe incorrect'
        };
        
        let errorMsg = errorData.error || errorData.detail || errorData.message;
        
        if (errorMsg) {
          Object.keys(errorMessages).forEach(pattern => {
            if (errorMsg.includes(pattern)) {
              errorMsg = errorMessages[pattern];
            }
          });
          generalError = errorMsg;
        }
      } else if (err.request) {
        generalError = 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.';
      }

      setStatusMessage({ type: 'error', text: generalError });
      setShakeIcon(true);
      setTimeout(() => setShakeIcon(false), 500);
      
    } finally {
      if (statusMessage.type !== 'success') {
         setLoading(false);
      }
    }
  };

  const renderAlert = (type, text) => {
    const isError = type === 'error';
    const alertClasses = isError ? 'bg-red-500/20 border-red-400/30' : 'bg-green-500/20 border-green-400/30';
    const icon = isError ? <AlertCircle size={20} className="text-red-300 mr-3 flex-shrink-0" /> : <CheckCircle size={20} className="text-green-300 mr-3 flex-shrink-0" />;
    const textColor = isError ? 'text-red-100' : 'text-green-100';

    return (
      <div className={`backdrop-blur-md border rounded-2xl p-4 mb-6 flex items-center justify-between ${alertClasses} ${isError ? 'shake-animation' : ''}`}>
        <div className="flex items-center">
            {icon}
            <span className={`text-sm ${textColor}`}>{text}</span>
        </div>
        {isError && (
          <button onClick={() => setStatusMessage({ type: null, text: '' })} className={`p-1 rounded-full text-red-300 hover:bg-red-500/30 transition-colors`}>
              <X size={16} />
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <style>{styles}</style>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-400/10 rounded-full blur-3xl"></div>

        <div className="bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl max-w-md w-full p-8 border border-white/20 relative z-10">
          <div className="text-center mb-8">
            <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-600 rounded-3xl mb-4 shadow-xl icon-container ${shakeIcon ? 'shake-animation' : ''} ${statusMessage.type === 'success' ? 'pulse-glow-animation' : ''}`}>
              <LogIn size={36} className="text-white" />
            </div>
            <h2 className="text-4xl font-bold text-white mb-2">Connexion</h2>
            <p className="text-gray-300">Accédez à votre compte MonMarket</p>
          </div>

          {statusMessage.text && renderAlert(statusMessage.type, statusMessage.text)}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Nom d'utilisateur
              </label>
              <div className="relative">
                <User className={`absolute left-4 top-4 text-gray-400 ${shakeIcon && statusMessage.type === 'error' ? 'wiggle-animation' : ''}`} size={20} />
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl focus:ring-2 focus:ring-orange-400 focus:border-transparent text-white placeholder-gray-400 transition-all"
                  placeholder="Entrez votre nom d'utilisateur"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className={`absolute left-4 top-4 text-gray-400 ${shakeIcon && statusMessage.type === 'error' ? 'wiggle-animation' : ''}`} size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-12 pr-12 py-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl focus:ring-2 focus:ring-orange-400 focus:border-transparent text-white placeholder-gray-400 transition-all"
                  placeholder="Entrez votre mot de passe"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-4 text-gray-400 hover:text-white transition"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="remember"
                  className="w-4 h-4 text-orange-500 bg-white/10 border-white/20 rounded focus:ring-orange-400"
                />
                <label htmlFor="remember" className="ml-2 text-sm text-gray-300">
                  Se souvenir de moi
                </label>
              </div>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-orange-400 hover:text-orange-300 transition"
              >
                Mot de passe oublié ?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading || statusMessage.type === 'success'}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-2xl font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-xl hover:shadow-2xl transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {(loading || statusMessage.type === 'success') ? (
                <>
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {statusMessage.type === 'success' ? 'Redirection...' : 'Connexion...'}
                </>
              ) : (
                <>
                  <LogIn size={22} className="mr-2" />
                  Se connecter
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-300">
              Pas encore de compte ?{' '}
              <button onClick={() => navigate('/register')} className="text-orange-400 font-semibold hover:text-orange-300 transition">
                S'inscrire
              </button>
            </p>
          </div>

          <div className="mt-4 text-center">
            <button onClick={() => navigate('/')} className="text-sm text-gray-400 hover:text-white transition">
              ← Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    </>
  );
}